'use client';

import { Card } from '../../components/card';
import { Text } from '../../components/text';
import { MarkdownPreview } from '..';
import { EnhancedCollapsibleWrapper } from '../EnhancedCollapsibleWrapper';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  TestTube01Icon,
  File01Icon,
  IdCardLanyardIcon,
  InformationCircleIcon,
  MicroscopeIcon,
  Shield01Icon,
  Target03Icon,
} from '@terra/ui-icons';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type { SignalDto } from '@terra/shared-contracts-signals';

interface SignalSummaryProps {
  signal: SignalDto;
}

export function SignalSummary({ signal }: SignalSummaryProps) {
  const t = useTranslations('designSystem.findings.findingDetails');
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [securityTestCaseOpen, setSecurityTestCaseOpen] = useState(false);
  const [smokeTestOpen, setSmokeTestOpen] = useState(false);

  return (
    <Card.Root w="full" borderRadius="lg" boxShadow="sm">
      <Card.Header>
        <HStack gap={2} alignItems="center">
          <Target03Icon size={16} />
          <Text fontSize="md" fontWeight="semibold">
            {t('relatedSignal')}
          </Text>
        </HStack>
      </Card.Header>

      <Card.Body>
        <VStack gap={4} alignItems="stretch">
          <Text fontSize="sm" color="gray.11">
            {t('findingGeneratedFromSignal')}
          </Text>

          <VStack gap={2} alignItems="stretch">
            {signal.description && (
              <EnhancedCollapsibleWrapper
                open={descriptionOpen}
                triggerIcon={<File01Icon size={16} />}
                triggerContent={t('signalSummary.sections.description')}
                onOpenChange={setDescriptionOpen}
                colorScheme="blue"
              >
                <Text fontSize="sm" color="gray.11" lineHeight="relaxed">
                  {signal.description}
                </Text>
              </EnhancedCollapsibleWrapper>
            )}

            {signal.analysis && (
              <EnhancedCollapsibleWrapper
                open={analysisOpen}
                triggerIcon={<MicroscopeIcon size={16} />}
                triggerContent={t('signalSummary.sections.analysis')}
                onOpenChange={setAnalysisOpen}
                colorScheme="purple"
              >
                <MarkdownPreview source={signal.analysis ?? ''} />
              </EnhancedCollapsibleWrapper>
            )}

            {signal.reason && (
              <EnhancedCollapsibleWrapper
                open={reasonOpen}
                triggerIcon={<InformationCircleIcon size={16} />}
                triggerContent={t('signalSummary.sections.confidenceReason')}
                onOpenChange={setReasonOpen}
                colorScheme="orange"
              >
                <MarkdownPreview source={signal.reason ?? ''} />
              </EnhancedCollapsibleWrapper>
            )}

            {signal.securityTestCaseSummary && (
              <EnhancedCollapsibleWrapper
                open={securityTestCaseOpen}
                triggerIcon={<Shield01Icon size={16} />}
                triggerContent={t('signalSummary.sections.securityTestCase')}
                onOpenChange={setSecurityTestCaseOpen}
                colorScheme="red"
              >
                <MarkdownPreview
                  source={signal.securityTestCaseSummary}
                  showCodeLineNumbers
                />
              </EnhancedCollapsibleWrapper>
            )}

            {signal.smokeTestSummary && (
              <EnhancedCollapsibleWrapper
                open={smokeTestOpen}
                triggerIcon={<TestTube01Icon size={16} />}
                triggerContent={t('signalSummary.sections.smokeTestSummary')}
                onOpenChange={setSmokeTestOpen}
                colorScheme="green"
              >
                <MarkdownPreview source={signal.smokeTestSummary} />
              </EnhancedCollapsibleWrapper>
            )}
          </VStack>

          <VStack
            gap={2}
            alignItems="start"
            pt={2}
            borderTopWidth={1}
            borderColor="gray.6"
          >
            {signal.lastSmokeTestedAt && (
              <HStack gap={2} fontSize="xs" color="gray.10">
                <TestTube01Icon size={12} />
                <Text fontWeight="medium">
                  {t('signalSummary.metadata.lastSmokeTested', {
                    date: new Date(signal.lastSmokeTestedAt).toLocaleString(),
                  })}
                </Text>
              </HStack>
            )}

            <HStack gap={2} fontSize="xs" color="gray.10">
              <IdCardLanyardIcon size={12} />
              <Text
                fontFamily="mono"
                bg="gray.3"
                px={2}
                py={1}
                rounded="md"
                fontWeight="medium"
              >
                {t('signalSummary.metadata.signalId', { id: signal.id })}
              </Text>
            </HStack>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}
